import React, { useState, useRef, useEffect } from 'react';
import locationAreaInfo from '../data/locationareainfo.json';
import generalServicesSkills from '../data/generalservicesskills.json';

const placeholderImg = '/defaultimages/imagenotavailable.jpg';

const GeneralServices = () => {
  // State management
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [location, setLocation] = useState('');
  const [area, setArea] = useState('');
  const [type, setType] = useState('');
  const [modalItem, setModalItem] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Ref for horizontally scrolling featured row
  const featuredScrollRef = useRef(null);

  const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

  // Fetch services from API
  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    if (!API_BASE) {
      setLoading(false);
      setError('API base URL is not configured. Please set REACT_APP_API_BASE in .env.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/api/generalservices`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
      
      const data = await response.json();
      setServices(data);
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const scrollFeaturedBy = (delta) => {
    if (featuredScrollRef.current) {
      featuredScrollRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  const getItemImages = (item) => {
    const imageSet = new Set();

    if (item?.imagepath && typeof item.imagepath === 'string') {
      imageSet.add(item.imagepath.trim());
    }

    let photos = item?.photos;
    if (typeof photos === 'string') {
      try {
        photos = JSON.parse(photos);
      } catch (e) {
        photos = photos
          .split(',')
          .map(p => p.trim())
          .filter(Boolean);
      }
    }

    if (Array.isArray(photos)) {
      photos.forEach((photo) => {
        if (typeof photo === 'string' && photo.trim()) {
          imageSet.add(photo.trim());
        }
      });
    }

    return Array.from(imageSet).filter(Boolean);
  };

  // Modal handlers
  const handleViewDetails = (item) => {
    const images = getItemImages(item);
    setModalItem({ ...item, images });
    setModalImageIndex(0);
    setShowShareMenu(false);
  };

  const closeModal = () => {
    setModalItem(null);
    setModalImageIndex(0);
    setShowShareMenu(false);
  };

  // Extract location and area options
  const locationOptions = (locationAreaInfo?.locationNames || []).slice().sort((a, b) => {
    const orderA = Number.isFinite(Number(a.listorder)) ? Number(a.listorder) : Number.MAX_SAFE_INTEGER;
    const orderB = Number.isFinite(Number(b.listorder)) ? Number(b.listorder) : Number.MAX_SAFE_INTEGER;
    return orderA - orderB || String(a.label || '').localeCompare(String(b.label || ''));
  });
  
  const areaOptions = Array.from(new Set(
    (locationAreaInfo?.areaNames || []).map(areaItem => areaItem.label)
  )).filter(Boolean).sort((a, b) => String(a).localeCompare(String(b)));
  
  const typeOptions = Array.from(
    new Set(
      (generalServicesSkills || [])
        .slice()
        .sort((a, b) => {
          const orderA = Number.isFinite(Number(a?.listorder)) ? Number(a.listorder) : Number.MAX_SAFE_INTEGER;
          const orderB = Number.isFinite(Number(b?.listorder)) ? Number(b.listorder) : Number.MAX_SAFE_INTEGER;
          const labelA = typeof a === 'string' ? a : (a?.businesstype || '');
          const labelB = typeof b === 'string' ? b : (b?.businesstype || '');
          return orderA - orderB || String(labelA).localeCompare(String(labelB));
        })
        .map((item) => (typeof item === 'string' ? item : item?.businesstype))
        .filter(Boolean)
    )
  );

  // Get name suggestions based on search input
  const suggestions = search.length > 0
    ? Array.from(new Set(
        services
          .filter(s => s.businessname && s.businessname.toLowerCase().includes(search.toLowerCase()))
          .map(s => s.businessname)
      )).slice(0, 8)
    : [];

  // Filter logic
  let filteredServices = services;
  if (location !== '') {
    filteredServices = filteredServices.filter(s => 
      (s.locationname?.split(',')[1]?.trim() || s.locationname?.split(',')[0]?.trim()) === location
    );
  }
  if (area !== '') {
    filteredServices = filteredServices.filter(s => 
      s.locationname?.split(',')[0]?.trim() === area
    );
  }
  if (type !== '') {
    filteredServices = filteredServices.filter(s => s.businesstype === type);
  }
  if (search.trim() !== '') {
    filteredServices = filteredServices.filter(s =>
      (s.businessname && s.businessname.toLowerCase().includes(search.trim().toLowerCase())) ||
      (s.businesstype && s.businesstype.toLowerCase().includes(search.trim().toLowerCase())) ||
      (s.aboutdescription && s.aboutdescription.toLowerCase().includes(search.trim().toLowerCase()))
    );
  }

  // Featured services
  const featuredServices = services.filter(s => s.featuredlisting === true || s.featuredlisting === 1);

  // Loading state
  if (loading) {
    return (
      <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{ fontSize: 18, color: '#666' }}>Loading services...</div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16, color: '#d32f2f' }}>⚠️</div>
        <div style={{ fontSize: 18, marginBottom: 8, color: '#d32f2f' }}>Error loading services</div>
        <div style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>{error}</div>
        <button
          onClick={fetchServices}
          style={{
            padding: '10px 20px',
            fontSize: 14,
            background: '#1976d2',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '100vw', margin: 0, padding: 10, boxSizing: 'border-box', overflowX: 'hidden' }}>
      {/* Header */}
      <header style={{ background: '#e8edf3ff', color: '#37864fff', padding: '0.25rem', textAlign: 'center', borderRadius: 8, marginBottom: 2 }}>
        <h1 style={{ margin: 0, fontSize: '1.0rem', fontWeight: 700 }}>Local Services</h1>
      </header>

      {/* Featured Section */}
      {featuredServices.length > 0 && (
        <div style={{ marginBottom: '0.2rem', position: 'relative' }}>
          
          {/* Left scroll button */}
          <button
            aria-label="Scroll featured left"
            onClick={() => scrollFeaturedBy(-250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              left: 0,
              zIndex: 2,
              background: '#fff',
              color: '#1976d2',
              border: '2px solid #1976d2',
              borderRadius: '50%',
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              transition: 'background 0.2s, color 0.2s, border 0.2s'
            }}
          >
            <span className="material-icons" style={{ fontSize: 18, lineHeight: 1 }}>chevron_left</span>
          </button>

          {/* Featured scroll container */}
          <div
            ref={featuredScrollRef}
            style={{
              display: 'flex',
              gap: 0,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              padding: '4px 28px',
              scrollSnapType: 'x mandatory',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none'
            }}
          >
            {featuredServices.map((item) => (
              <div
                  key={item.bid}
                  onClick={() => handleViewDetails(item)}
                  style={{
                    minWidth: 115.0,
                    maxWidth: 115.0,
                    background: '#fff',
                    border: '0.5px solid #1976d2',
                    borderRadius: 8,
                    padding: 6,
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    scrollSnapAlign: 'start',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                  }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
              >
                <img
                  src={item.imagepath || placeholderImg}
                  alt={item.businessname}
                  style={{ width: '95%', height: 75, objectFit: 'cover', borderRadius: 6, marginBottom: 4 }}
                  onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                />
                <h3 style={{ margin: 0, fontSize: 13, color: '#1976d2' }}>{item.businessname}</h3>
                {item.businesstype && <div style={{ fontSize: 11, color: '#666', marginBottom: 4 }}>{item.businesstype}</div>}
                {item.contactperson && (
                  <div style={{ fontSize: 11, color: '#777', marginBottom: 4 }}>
                    Contact: <span style={{ fontWeight: 600, color: '#555' }}>{item.contactperson}</span>
                  </div>
                )}
                <div style={{ fontSize: 10, color: '#888', marginBottom: 6, fontWeight: 600 }}>
                  {item.locationname} • {item.areaname}
                </div>
                {/* Social media icons */}
                {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    {item.sm_instagram && (
                      <a
                        href={`https://instagram.com/${item.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {item.sm_facebook && (
                      <a
                        href={`https://facebook.com/${item.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {item.sm_youtube && (
                      <a
                        href={`https://youtube.com/${item.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          borderRadius: 4,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="YouTube"
                      >
                        ▶
                      </a>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Right scroll button */}
          <button
            aria-label="Scroll featured right"
            onClick={() => scrollFeaturedBy(250)}
            style={{
              position: 'absolute',
              top: '35%',
              transform: 'translateY(-50%)',
              right: 0,
              zIndex: 2,
              background: '#fff',
              color: '#1976d2',
              border: '2px solid #1976d2',
              borderRadius: '50%',
              width: 28,
              height: 28,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
              transition: 'background 0.2s, color 0.2s, border 0.2s'
            }}
          >
            <span className="material-icons" style={{ fontSize: 18, lineHeight: 1 }}>chevron_right</span>
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div style={{ marginBottom: '1rem', marginTop: '0.2rem', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Search bar with suggestions */}
        <div style={{ position: 'relative', width: '50%' }}>
          <input
            type="text"
            placeholder="Search by name or skill..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            style={{
              width: '100%',
              padding: '8px 12px',
              fontSize: 14,
              border: '1px solid #ccc',
              borderRadius: 8,
              boxSizing: 'border-box'
            }}
          />
          {showSuggestions && suggestions.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              background: '#fff',
              border: '1px solid #ccc',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              maxHeight: 200,
              overflowY: 'auto',
              zIndex: 10,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }}>
              {suggestions.map((name, idx) => (
                <div
                  key={idx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setSearch(name);
                    setShowSuggestions(false);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setSearch(name);
                    setShowSuggestions(false);
                  }}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    fontSize: 14,
                    borderBottom: idx < suggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                    background: '#fff'
                  }}
                >
                  {name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Filter dropdowns */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'nowrap', maxWidth: '50%' }}>
          <select
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            style={{ flex: 1, minWidth: 90, maxWidth: 130, padding: '6px', fontSize: 13, border: '1px solid #ccc', borderRadius: 6 }}
          >
            <option value="">All Locations</option>
            {locationOptions.map(loc => <option key={loc.id || loc.value || loc.label} value={loc.label}>{loc.label}</option>)}
          </select>

          <select
            value={area}
            onChange={(e) => setArea(e.target.value)}
            style={{ flex: 1, minWidth: 90, maxWidth: 130, padding: '6px', fontSize: 13, border: '1px solid #ccc', borderRadius: 6 }}
          >
            <option value="">All Areas</option>
            {areaOptions.map(ar => <option key={ar} value={ar}>{ar}</option>)}
          </select>

          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{ flex: 1, minWidth: 90, maxWidth: 130, padding: '6px', fontSize: 13, border: '1px solid #ccc', borderRadius: 6 }}
          >
            <option value="">All Skills</option>
            {typeOptions.map(tp => <option key={tp} value={tp}>{tp}</option>)}
          </select>
        </div>

        {/* Active filters indicator */}
        {(search || location || area || type) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: '#666' }}>Active filters:</span>
            {search && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Search: {search}</span>}
            {location && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Location: {location}</span>}
            {area && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Area: {area}</span>}
            {type && <span style={{ background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 12 }}>Skill: {type}</span>}
            <button
              onClick={() => { setSearch(''); setLocation(''); setArea(''); setType(''); }}
              style={{ background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Results count */}
      <div style={{ marginBottom: '1rem', fontSize: 14, color: '#666' }}>
        {filteredServices.length} {filteredServices.length === 1 ? 'service' : 'services'} found
      </div>

      {/* Services Grid */}
      {filteredServices.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <div style={{ fontSize: 18, marginBottom: 8 }}>No services found</div>
          <div style={{ fontSize: 14 }}>Try adjusting your search or filters</div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 8,
          marginBottom: 20
        }}>
          {filteredServices.map((item) => (
            <div
              key={item.bid}
              onClick={() => handleViewDetails(item)}
              style={{
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 12,
                padding: 12,
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)';
              }}
            >
              <img
                src={item.imagepath || placeholderImg}
                alt={item.businessname}
                style={{ width: 115, height: 120, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: 15, color: '#1976d2' }}>{item.businessname}</h3>
                {item.businesstype && <div style={{ fontSize: 13, color: '#666', marginBottom: 4, fontWeight: 500 }}>{item.businesstype}</div>}
                {item.contactperson && (
                  <div style={{ fontSize: 13, color: '#444', marginBottom: 4 }}>
                    Contact: <span style={{ fontWeight: 600 }}>{item.contactperson}</span>
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8, fontWeight: 600 }}>
                  {item.locationname} • {item.areaname}
                </div>
                <p style={{
                  margin: '0 0 8px 0',
                  fontSize: 14,
                  color: '#555',
                  lineHeight: 1.4,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                  {item.aboutdescription}
                </p>
                {/* Social media icons */}
                {(item.sm_instagram || item.sm_facebook || item.sm_youtube) && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    {item.sm_instagram && (
                      <a
                        href={`https://instagram.com/${item.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 5,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {item.sm_facebook && (
                      <a
                        href={`https://facebook.com/${item.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 5,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {item.sm_youtube && (
                      <a
                        href={`https://youtube.com/${item.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 5,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif'
                        }}
                        title="YouTube"
                      >
                        ▶
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalItem && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              maxWidth: 450,
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
            }}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                background: 'rgba(0,0,0,0.5)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: 36,
                height: 36,
                fontSize: 24,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
              }}
            >
              ×
            </button>

            {/* Modal content */}
            {(() => {
              const modalImages = modalItem.images && modalItem.images.length > 0 ? modalItem.images : [modalItem.imagepath || placeholderImg];
              const safeIndex = Math.min(modalImageIndex, modalImages.length - 1);
              const activeImage = modalImages[safeIndex] || placeholderImg;

              return (
                <div style={{ marginTop: 12, position: 'relative' }}>
                  <img
                    src={activeImage}
                    alt={modalItem.businessname}
                    style={{
                      width: '90%',
                      maxWidth: 400,
                      height: 240,
                      objectFit: 'cover',
                      borderRadius: 12,
                      display: 'block',
                      margin: '0 auto'
                    }}
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                  />

                  {modalImages.length > 1 && (
                    <>
                      <button
                        onClick={() => setModalImageIndex(prev => (prev > 0 ? prev - 1 : modalImages.length - 1))}
                        style={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          border: 'none',
                          background: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        aria-label="Previous image"
                      >
                        ‹
                      </button>

                      <button
                        onClick={() => setModalImageIndex(prev => (prev < modalImages.length - 1 ? prev + 1 : 0))}
                        style={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          border: 'none',
                          background: 'rgba(0,0,0,0.55)',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        aria-label="Next image"
                      >
                        ›
                      </button>

                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 6,
                        marginTop: 8,
                        flexWrap: 'wrap'
                      }}>
                        {modalImages.map((img, idx) => (
                          <img
                            key={`${modalItem.bid}-thumb-${idx}`}
                            src={img}
                            alt={`${modalItem.businessname} ${idx + 1}`}
                            onClick={() => setModalImageIndex(idx)}
                            style={{
                              width: 46,
                              height: 46,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: idx === safeIndex ? '2px solid #1976d2' : '1px solid #ddd',
                              cursor: 'pointer'
                            }}
                            onError={(e) => { e.target.onerror = null; e.target.src = placeholderImg; }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}
            
            <div style={{ padding: '8px 20px 20px 20px' }}>
              {(modalItem.featuredlisting === 1 || modalItem.featuredlisting === true) && (
                <div style={{ fontSize: 11, color: '#fff', background: '#388e3c', padding: '4px 10px', borderRadius: 4, float: 'right', marginBottom: 6, fontWeight: 600 }}>
                  Featured
                </div>
              )}
              
              <h2 style={{ margin: '0 0 2px 0', fontSize: 18, color: '#1976d2' }}>{modalItem.businessname}</h2>
              
              {modalItem.businesstype && (
                <div style={{ fontSize: 12, color: '#666', marginBottom: 5, fontWeight: 500 }}>{modalItem.businesstype}</div>
              )}
              
              <div style={{ fontSize: 14, color: '#888', marginBottom: 2, fontWeight: 600 }}>
                 {modalItem.locationname?.split(',')[0]?.trim()} - {modalItem.areaname?.split(',')[0]?.trim()}
              </div>
              {/* locationcode intentionally not displayed */}
              
              <div style={{ marginTop: 4, marginBottom: 4 }}>
                  <strong style={{ fontSize: '0.90rem', color: '#333', display: 'inline' }}>Description:</strong>
                  <span style={{ marginLeft: 6, color: '#333', lineHeight: 1.4, fontSize: '0.90rem', display: 'inline' }}>{modalItem.aboutdescription}</span>
                </div>
              
              {modalItem.contactnumber && (
                <div style={{ marginBottom: 6 }}>
                  <strong style={{ fontSize: 15, color: '#333' }}>Contact Details:</strong>
                  {modalItem.contactperson && (
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#666', marginTop: 4 }}>
                      Name: <span style={{ fontWeight: 600, color: '#555' }}>{modalItem.contactperson}</span>
                    </div>
                  )}
                  <a 
                    href={`tel:${modalItem.contactnumber.replace(/\s/g, '')}`}
                    style={{ 
                      fontSize: 14, 
                      color: '#d32f2f', 
                      marginTop: 4, 
                      fontWeight: 600,
                      textDecoration: 'none',
                      display: 'block',
                      cursor: 'pointer'
                    }}
                  >
                    📞 {modalItem.contactnumber}
                  </a>
                </div>
              )}

              {modalItem.emailid && (
                <div style={{ marginBottom: 2 }}>
                  <strong style={{ fontSize: 14, color: '#333' }}>Email:</strong>
                  <a href={`mailto:${modalItem.emailid}`} style={{ fontSize: 14, color: '#1976d2', textDecoration: 'none', marginLeft: 8 }}>{modalItem.emailid}</a>
                </div>
              )}

              {modalItem.address1 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14, color: '#333' }}>Address:</strong>
                    <span style={{ fontSize: 14, color: '#666' }}>{modalItem.address1}</span>
                  </div>
                </div>
              )}

              {/* Call Now and Share buttons */}
              <div style={{ display: 'flex', gap: 12, marginTop: 12, marginBottom: 8, justifyContent: 'center' }}>
                {/* Call Now button */}
                {modalItem.contactnumber && (
                  <button
                    onClick={() => window.location.href = `tel:${modalItem.contactnumber.replace(/\s/g, '')}`}
                    style={{
                      padding: '8px 16px',
                      background: '#4CAF50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#45a049'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#4CAF50'}
                  >
                    📞 Call
                  </button>
                )}
                
                {/* Share button with dropdown */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowShareMenu(!showShareMenu)}
                    style={{
                      padding: '8px 16px',
                      background: '#1976d2',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 5,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 4,
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#1565c0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#1976d2'}
                  >
                    🔗 Share
                  </button>
                  
                  {/* Share dropdown menu */}
                  {showShareMenu && (
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: 0,
                      right: 0,
                      marginBottom: 8,
                      background: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: 8,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 100,
                      overflow: 'hidden'
                    }}>
                      {/* WhatsApp option (default) */}
                      <button
                        onClick={() => {
                          const shareText = `*${modalItem.businessname}*\n${modalItem.businesstype || ''}\n📍 ${modalItem.locationname || ''}\n📞 ${modalItem.contactnumber || ''}${modalItem.emailid ? `\n✉️ ${modalItem.emailid}` : ''}`;
                          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
                          window.open(whatsappUrl, '_blank');
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#25D366',
                          color: '#fff',
                          border: 'none',
                          borderBottom: '1px solid #eee',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#20ba5a'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#25D366'}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                      </button>
                      
                      {/* Email option */}
                      <button
                        onClick={() => {
                          const subject = encodeURIComponent(`Check out ${modalItem.businessname}`);
                          const body = encodeURIComponent(
                            `${modalItem.businessname}\n${modalItem.businesstype || ''}\n\nLocation: ${modalItem.locationname || ''}\n${modalItem.address1 ? `Address: ${modalItem.address1}\n` : ''}Contact: ${modalItem.contactnumber || ''}\n${modalItem.emailid ? `Email: ${modalItem.emailid}\n` : ''}\n${modalItem.aboutdescription ? `\nAbout:\n${modalItem.aboutdescription}` : ''}`
                          );
                          window.location.href = `mailto:?subject=${subject}&body=${body}`;
                          setShowShareMenu(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 16px',
                          background: '#fff',
                          color: '#333',
                          border: 'none',
                          fontSize: 24,
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                      >
                        ✉️
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Social media links */}
              {(modalItem.sm_instagram || modalItem.sm_facebook || modalItem.sm_youtube) && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <strong style={{ fontSize: 14, color: '#333' }}>Connect:</strong>
                    <div style={{ display: 'flex', gap: 12 }}>
                    {modalItem.sm_instagram && (
                      <a
                        href={`https://instagram.com/${modalItem.sm_instagram}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 5,
                          background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        title="Instagram"
                      >
                        IG
                      </a>
                    )}
                    {modalItem.sm_facebook && (
                      <a
                        href={`https://facebook.com/${modalItem.sm_facebook}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 5,
                          background: '#1877F2',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        title="Facebook"
                      >
                        f
                      </a>
                    )}
                    {modalItem.sm_youtube && (
                      <a
                        href={`https://youtube.com/${modalItem.sm_youtube}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 28,
                          height: 28,
                          borderRadius: 5,
                          background: '#FF0000',
                          color: '#fff',
                          fontSize: 13,
                          fontWeight: 'bold',
                          textDecoration: 'none',
                          fontFamily: 'Arial, sans-serif',
                          transition: 'transform 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                        title="YouTube"
                      >
                        ▶
                      </a>
                    )}
                  </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneralServices;
